import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

const backgroundImages = [
  "/renamed_website_images/biology-lab-1.jpg",
  "/renamed_website_images/laboratory-gallery-01.jpg",
  "/renamed_website_images/laboratory-gallery-05.jpg",
];

const cards = [
  {
    img: "/renamed_website_images/biology-lab-1.jpg",
    title: "Microscopic Studies",
    desc: "High-precision compound microscopes for studying cell structures, tissues, and microorganisms.",
  },
  {
    img: "/renamed_website_images/laboratory-gallery-01.jpg",
    title: "Anatomy & Specimen Models",
    desc: "Detailed anatomical charts, skeletal structures, and botanical preserved specimens.",
  },
  {
    img: "/renamed_website_images/laboratory-gallery-05.jpg",
    title: "Hands-On Experiments",
    desc: "Supervised practical sessions fostering biological curiosity and scientific methodology.",
  },
];

const BiologyLab = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden bg-[#020810]">
      {/* Smooth Cross-fade Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {backgroundImages.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: currentImageIndex === idx ? 1 : 0,
              transition: "opacity 1.5s ease-in-out",
            }}
          />
        ))}
      </div>

      {/* Dark overlay */}
      <div
        className="absolute inset-0 z-1"
        style={{ background: "rgba(2,8,16,0.88)" }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Biology Lab
        </h1>
        <div className="cosmic-divider mb-10" />

        {/* Intro */}
        <div
          className="glass-card p-8 mb-10 grid md:grid-cols-2 gap-8 items-center"
          style={{ borderColor: "rgba(34,197,94,0.2)" }}
        >
          <div className="overflow-hidden rounded-xl h-64 border border-white/10 shadow-lg">
            <img
              src="/renamed_website_images/biology-lab-1.jpg"
              alt="Biology Lab Setup"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-xl font-bold text-emerald-400 mb-3" style={{ fontFamily: "Cinzel, serif" }}>
              Explore Life Sciences & Anatomy
            </h3>
            <p
              className="leading-relaxed mb-4"
              style={{
                fontFamily: "Nunito, sans-serif",
                color: "#8899b8",
                fontSize: "0.95rem",
              }}
            >
              Our Biology Lab is a vibrant space designed for young scientists to explore life sciences in depth. Equipped with high-quality microscopes, anatomical models, and botanical specimens, students observe living cells, tissues, and biological processes firsthand.
            </p>
            <p
              className="leading-relaxed"
              style={{
                fontFamily: "Nunito, sans-serif",
                color: "#8899b8",
                fontSize: "0.95rem",
              }}
            >
              The lab supports ICSE curriculum-based experiments as well as independent biological research under the guidance of experienced faculty.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {cards.map((card, i) => (
            <div
              key={i}
              className="glass-card overflow-hidden transition-all duration-300 border"
              style={{ borderColor: "rgba(34,197,94,0.25)" }}
            >
              <div className="h-52 overflow-hidden">
                <img
                  src={card.img}
                  alt={card.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 text-center">
                <h3
                  className="font-bold text-emerald-400 mb-2"
                  style={{ fontFamily: "Cinzel, serif" }}
                >
                  {card.title}
                </h3>
                <p className="text-xs text-gray-400 leading-relaxed">
                  {card.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-6 border-t border-white/10">
          <Link
            to="/facilities/sports"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Sports
          </Link>
          <Link
            to="/facilities/chemistry-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Chemistry Lab <FaArrowRight />
          </Link>
        </div>
      </div>

      {/* Side floating arrows */}
      <Link
        to="/facilities/sports"
        aria-label="Previous Lab: Sports"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/chemistry-lab"
        aria-label="Next Lab: Chemistry Lab"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default BiologyLab;
